import { Component, OnInit, ElementRef, HostListener, inject } from '@angular/core';
import { Api } from '../../services/api';
import { AlertService } from '../../services/alert.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MdlManageProspect } from '../../components/mdl-manage-prospect/mdl-manage-prospect';
import { MdlDelete } from '../../components/mdl-delete/mdl-delete';
import { MdlChartInfo } from '../../components/mdl-chart-info/mdl-chart-info';
import { PermissionService } from '../../services/permission.service';
import { jwtDecode } from 'jwt-decode';
import { MdlChangeOwnerComponent } from '../../components/mdl-change-owner/mdl-change-owner';
import { MdlFilterProspects } from '../../components/mdl-filter-prospects/mdl-filter-prospects';
import { MdlSendMail } from '../../components/mdl-send-mail/mdl-send-mail';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-prospects',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './prospects.html',
  styleUrls: ['./prospects.css'],
})
export class Prospects implements OnInit {
  prospectos: any[] = [];
  prospectosFiltrados: any[] = [];
  isLoading: boolean = true;
  errorMessage: string = '';
  showRecentDropdown: boolean = false;
  selectedItem: string = 'todas';
  openedMenuId: string | null = null;
  searchTerm: string = '';
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  activeFilters: any = null;
  public industrias: any[] = [];
  private alertService = inject(AlertService);

  constructor(
    private api: Api,
    private elementRef: ElementRef,
    private router: Router,
    public permissionService: PermissionService,
  ) {
    this.cargarProspectos();
  }

  readonly dialog = inject(MatDialog);

  ngOnInit(): void {
    this.cargarProspectos();
    this.initializeFilters();
    console.log("local", localStorage);
  }
  
  private initializeFilters(): void {
    if (!this.activeFilters) {
      this.activeFilters = {
        allCompanies: true,
        allPositions: true,
        allStatus: true,
        creationDateRange: 'all',
        phoneFilter: 'all',
        emailFilter: 'all',
        companies: {},
        positions: {},
        status: {}
      };
    }
  }

  openFilterModal(): void {
    const dialogRef = this.dialog.open(MdlFilterProspects, {
      minWidth: '600px',
      maxHeight: '80vh',
      panelClass: 'mat-dlg-class',
      data: {
        prospectos: this.prospectos,
        filters: this.activeFilters
      }
    });

    dialogRef.afterClosed().subscribe((filters: any) => {
      if (filters) {
        this.activeFilters = filters;
        this.applyAdvancedFilters();
        this.alertService.success('Éxito', 'Filtros aplicados correctamente');
      }
    });
  }

  private applyAdvancedFilters(): void {
    let filteredProspects = [...this.prospectos];

    if (!this.activeFilters.allCompanies) {
      const selectedCompanies = Object.keys(this.activeFilters.companies).filter(key => this.activeFilters.companies[key]);
      if (selectedCompanies.length > 0) {
        filteredProspects = filteredProspects.filter(prospecto => 
          selectedCompanies.includes(prospecto.empresa)
        );
      }
    }

    if (!this.activeFilters.allPositions) {
      const selectedPositions = Object.keys(this.activeFilters.positions).filter(key => this.activeFilters.positions[key]);
      if (selectedPositions.length > 0) {
        filteredProspects = filteredProspects.filter(prospecto => 
          selectedPositions.includes(prospecto.cargo)
        );
      }
    }

    if (!this.activeFilters.allStatus) {
      const selectedStatus = Object.keys(this.activeFilters.status).filter(key => this.activeFilters.status[key]);
      if (selectedStatus.length > 0) {
        filteredProspects = filteredProspects.filter(prospecto => 
          selectedStatus.includes(prospecto.estado)
        );
      }
    }

    if (this.activeFilters.creationDateRange !== 'all') {
      filteredProspects = this.filterByCreationDate(filteredProspects, this.activeFilters.creationDateRange);
    }

    
    if (this.activeFilters.phoneFilter !== 'all') {
      filteredProspects = this.filterByPhone(filteredProspects, this.activeFilters.phoneFilter);
    }

    
    if (this.activeFilters.emailFilter !== 'all') {
      filteredProspects = this.filterByEmail(filteredProspects, this.activeFilters.emailFilter);
    }

    this.prospectosFiltrados = filteredProspects;
    
    if (this.sortColumn) {
      this.applySorting();
    }
  }

  private filterByCreationDate(prospects: any[], dateRange: string): any[] {
    const hoy = new Date();
    
    switch (dateRange) {
      case 'today':
        return prospects.filter(prospecto => {
          if (!prospecto.fecha_creacion) return false;
          const fechaCreacion = new Date(prospecto.fecha_creacion);
          return fechaCreacion.toDateString() === hoy.toDateString();
        });
      
      case '7d':
        const sieteDiasAtras = new Date(hoy);
        sieteDiasAtras.setDate(hoy.getDate() - 7);
        return prospects.filter(prospecto => {
          if (!prospecto.fecha_creacion) return false;
          const fechaCreacion = new Date(prospecto.fecha_creacion);
          return fechaCreacion >= sieteDiasAtras && fechaCreacion <= hoy;
        });
      
      case '30d':
        const treintaDiasAtras = new Date(hoy);
        treintaDiasAtras.setDate(hoy.getDate() - 30);
        return prospects.filter(prospecto => {
          if (!prospecto.fecha_creacion) return false;
          const fechaCreacion = new Date(prospecto.fecha_creacion);
          return fechaCreacion >= treintaDiasAtras && fechaCreacion <= hoy;
        });
      
      default:
        return prospects;
    }
  }

  private filterByPhone(prospects: any[], phoneFilter: string): any[] {
    switch (phoneFilter) {
      case 'withPhone':
        return prospects.filter(prospecto => prospecto.telefono && prospecto.telefono.trim() !== '');
      case 'withoutPhone':
        return prospects.filter(prospecto => !prospecto.telefono || prospecto.telefono.trim() === '');
      default:
        return prospects;
    }
  }

  private filterByEmail(prospects: any[], emailFilter: string): any[] {
    switch (emailFilter) {
      case 'withEmail':
        return prospects.filter(prospecto => prospecto.email && prospecto.email.trim() !== '');
      case 'withoutEmail':
        return prospects.filter(prospecto => !prospecto.email || prospecto.email.trim() === '');
      default:
        return prospects;
    }
  }

  sortByColumn(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortColumn) return;

    this.prospectosFiltrados = [...this.prospectosFiltrados].sort((a, b) => {
      let valueA = this.getSortValue(a, this.sortColumn);
      let valueB = this.getSortValue(b, this.sortColumn);

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  private getSortValue(prospecto: any, column: string): any {
    switch (column) {
      case 'nombre':
        return `${prospecto.nombre} ${prospecto.apellido}`.toLowerCase();
      
      case 'cargo':
        return prospecto.cargo?.toLowerCase() || '';
      
      case 'empresa':
        return prospecto.empresa?.toLowerCase() || '';
      
      case 'telefono':
        return prospecto.telefono || '';
      
      case 'email':
        return prospecto.email?.toLowerCase() || '';
      
      case 'estado':
        return prospecto.estado?.toLowerCase() || '';
      
      case 'ejecutivo.nombre':
        return `${prospecto.ejecutivo?.nombre || ''} ${prospecto.ejecutivo?.apellido || ''}`.toLowerCase();
      
      default:
        return prospecto[column] || '';
    }
  }

  onSearchChange(): void {
    if (!this.searchTerm.trim()) {
      if (this.activeFilters) {
        this.applyAdvancedFilters();
      } else {
        this.prospectosFiltrados = [...this.prospectos];
      }
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.prospectosFiltrados = this.prospectos.filter(prospecto => 
        prospecto.nombre?.toLowerCase().includes(searchLower) ||
        prospecto.apellido?.toLowerCase().includes(searchLower) ||
        prospecto.cargo?.toLowerCase().includes(searchLower) ||
        prospecto.empresa?.toLowerCase().includes(searchLower) ||
        prospecto.telefono?.includes(searchLower) ||
        prospecto.email?.toLowerCase().includes(searchLower) ||
        prospecto.estado?.toLowerCase().includes(searchLower) ||
        prospecto.ejecutivo?.nombre?.toLowerCase().includes(searchLower) ||
        prospecto.ejecutivo?.apellido?.toLowerCase().includes(searchLower)
      );
    }
    
    if (this.sortColumn) {
      this.applySorting();
    }
  }

  hasActiveFilters(): boolean {
    if (!this.activeFilters) return false;
    
    const hasCompanyFilter = !this.activeFilters.allCompanies && 
      Object.values(this.activeFilters.companies).some(val => val === true);
    const hasPositionFilter = !this.activeFilters.allPositions && 
      Object.values(this.activeFilters.positions).some(val => val === true);
    const hasStatusFilter = !this.activeFilters.allStatus && 
      Object.values(this.activeFilters.status).some(val => val === true);
    const hasDateFilter = this.activeFilters.creationDateRange !== 'all';
    const hasPhoneFilter = this.activeFilters.phoneFilter !== 'all';
    const hasEmailFilter = this.activeFilters.emailFilter !== 'all';
    
    return hasCompanyFilter || hasPositionFilter || hasStatusFilter || hasDateFilter || hasPhoneFilter || hasEmailFilter || this.searchTerm.trim() !== '';
  }

  clearFilters(): void {
    this.activeFilters = null;
    this.searchTerm = '';
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.initializeFilters();
    this.prospectosFiltrados = [...this.prospectos];
    this.alertService.success('Éxito', 'Filtros limpiados');
  }


  async cargarProspectos(): Promise<void> {
    this.api.showLoading();
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const response: any = await firstValueFrom(this.api.getProspects());
      
      this.prospectos = (response.prospectos || response).map((prospecto: any) => ({
        ...prospecto,
        id: prospecto.prospecto_id || prospecto.id,
        propietarioId: prospecto.ejecutivo_id
      }));

      if (this.activeFilters) {
        this.applyAdvancedFilters();
      } else {
        this.prospectosFiltrados = [...this.prospectos];
      }

      setTimeout(() => {
        this.api.hideLoading();
        this.isLoading = false;
      }, 500);

    } catch (error: any) {
      console.error('Error al cargar prospectos:', error);

      if (error.status === 403) {
        this.errorMessage = 'No tienes permiso para ver prospectos.';
      } else {
        this.errorMessage = 'Error al cargar la lista de prospectos.';
      }

      this.mostrarMensaje(this.errorMessage, 'error');

      setTimeout(() => {
        this.api.hideLoading();
        this.isLoading = false;
      }, 500);
    }
  }


  verDetalleProspecto(id: string, event?: MouseEvent): void {
    if (event) {
      const target = event.target as HTMLElement;
      const isCheckbox = target.tagName === 'INPUT' && target.getAttribute('type') === 'checkbox';
      const isDropdown = target.closest('.dropdown-container') !== null;
      
      if (isCheckbox || isDropdown) {
        return;
      }
    }

    if (!id || !this.esUUIDValido(id)) {
      this.mostrarMensaje('ID de prospecto no válido', 'error');
      return;
    }

    this.router.navigate(['prospect', id]).then(navigationResult => {
      if (!navigationResult) {
        this.mostrarMensaje('No se pudo cargar el detalle del prospecto', 'error');
        console.error('Error de navegación a prospects/', id);
      }
    });
  }

  private esUUIDValido(id: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }

  mostrarMensaje(mensaje: string, tipo: 'exito' | 'error' | 'advertencia'): void {
    switch (tipo) {
      case 'exito':
        this.alertService.success('Éxito', mensaje);
        break;
      case 'error':
        this.alertService.error('Error', mensaje);
        break;
      case 'advertencia':
        this.alertService.warning('Atención', mensaje);
        break;
    }
  }

  toggleRecentDropdown(): void {
    this.showRecentDropdown = !this.showRecentDropdown;
  }

  selectItem(key: string): void {
    this.selectedItem = key;
    this.showRecentDropdown = false; 
  }

  toggleActionsMenu(id: string): void {
    this.openedMenuId = this.openedMenuId === id ? null : id;
    this.showRecentDropdown = false; 
  }

  openMdlCreateProspect(){
    this.dialog.open(MdlManageProspect,{
      minWidth: '800px',
      maxHeight: '900px',
      panelClass: 'mat-dlg-class',
      data:{
        mode: 'create'
      }
    }).afterClosed().subscribe(async (result) => {
    if(result === true){
      await this.cargarProspectos();
      this.alertService.success('Éxito', 'El prospecto fue creado exitosamente.');
    }
  });
  }

  openManageProspect(prospecto: any): void {
    this.dialog.open(MdlManageProspect, {
      minWidth: '800px',
      maxHeight: '900px',
      panelClass: 'mat-dlg-class',
      data: {
        prospecto: prospecto,
        mode: 'edit'
      }
    }).afterClosed().subscribe((result) => {
    if (result===true) {
      this.cargarProspectos();
      this.alertService.success('Éxito', 'El prospecto fue modificado exitosamente.');

    }
  });

    
  }

  openMdlDeleteProspect(id:string){
    this.dialog.open(MdlDelete, {
      minWidth: '500px',
      minHeight: '260px',
      panelClass: 'mat-dlg-class',
      data:{
        mode: 'prospect',
        id: id
      }
    }).afterClosed().subscribe((result)=>{
      if (result===true){
        this.cargarProspectos();
        this.alertService.success('Éxito','El prospecto fue eliminado exitosamente.')
      }
    })
  }

  openMdlCharts(){
    this.dialog.open(MdlChartInfo, {
      minWidth: '500px',
      minHeight: '260px',
      panelClass: 'mat-dlg-class',
      data: {
        mode: 'pro'
      }
    });
  }

  openNewChangeOwner(prospecto: any) {
    console.log('Antes de abrir modal, prospecto:', prospecto); 
    console.log('Prospecto que envío al modal:', prospecto);
    this.dialog.open(MdlChangeOwnerComponent, {
      minWidth: '750px',
      maxHeight: '900px',
      panelClass: 'mat-dlg-class',
      data: { prospectData: prospecto }
      }).afterClosed().subscribe(result => {
        if (result) {
          console.log('Nuevo propietario:', result);
        }
      });
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: EventTarget | null): void {
    if (!(target instanceof HTMLElement)) return;
    
    const clickedInsideRecent = this.elementRef.nativeElement.querySelector('.dropdown-trigger')?.contains(target);
    const clickedInsideActions = this.elementRef.nativeElement.querySelector('.dots-button')?.contains(target);
    
    if (!clickedInsideRecent) {
      this.showRecentDropdown = false;
    }
    
    if (!clickedInsideActions) {
      this.openedMenuId = null;
    }
  }
}